import { useMemo, useState } from 'react';
import { Loader2, Sparkles, X } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import {
  PROVIDERS,
  TIER_LABEL,
  type Duration,
  type Resolution,
} from '@/lib/editorCapabilities';
import type { Video2dProviderId, VideoClipTransparency } from '@/services/avatarApi';

interface Props {
  open: boolean;
  isPending?: boolean;
  /** Server-side or transport error from the most recent submission attempt,
   *  shown as a visible banner so the user knows why "Generate" appeared to
   *  do nothing. */
  errorMessage?: string | null;
  onClose: () => void;
  onGenerate: (params: {
    prompt: string;
    provider: Video2dProviderId;
    durationSec: Duration;
    resolution: Resolution;
    variants: 1 | 2 | 3;
    transparency: VideoClipTransparency;
    seed?: number;
  }) => void;
}

const MAX_PROMPT_CHARS = 300;

/**
 * Phase 7a+ — 2D animation modal. The PROVIDERS capability matrix is now
 * shared with the Avatar Editor wizard via @/lib/editorCapabilities — one
 * source of truth for durations, resolutions, costs, and tier labels.
 */

function friendlyError(raw: string | null | undefined): { title: string; detail?: string } | null {
  if (!raw) return null;
  if (raw === 'user_daily_video2d_cap')         return { title: 'Daily clip limit reached', detail: 'You hit the 24h cap. Wait a bit or ask an admin to bump MAX_VIDEO2D_PER_USER_PER_DAY.' };
  if (raw === 'too_many_concurrent_videos')     return { title: 'Too many clips in flight', detail: 'You already have 3 clips generating. Wait for one to finish.' };
  if (raw === 'avatar_video_capacity_reached')  return { title: 'Avatar full of clips', detail: 'This avatar holds 20 clips already. Delete one or pick a different avatar.' };
  if (raw === 'prompt_too_short')               return { title: 'Motion prompt too short' };
  if (raw === 'prompt_too_long')                return { title: 'Motion prompt too long' };
  if (raw === 'no_source_image')                return { title: 'Avatar has no source image' };
  if (raw === 'avatar_not_active')              return { title: 'Avatar is not active' };
  return { title: "Couldn't submit", detail: raw };
}

export function Animate2dModal({ open, isPending, errorMessage, onClose, onGenerate }: Props) {
  const haptics = useHaptics();
  const [providerId, setProviderId] = useState<Video2dProviderId>('hailuo-fal');
  const [prompt, setPrompt] = useState('');
  const [durationSec, setDurationSec] = useState<Duration>(6);
  // Default to 512p — minimum resolution for Hailuo (cheapest test runs). The
  // safeResolution memo clamps to the provider's lowest option if the user
  // switches to a provider that doesn't support 512p.
  const [resolution, setResolution] = useState<Resolution>('512p');
  const [variants, setVariants] = useState<1 | 2 | 3>(1);
  // Seed: empty string = "random" (omit from payload), parsed int otherwise.
  const [seedInput, setSeedInput] = useState<string>('');
  // Default to true alpha (matting) — it's the highest-quality transparency
  // mode and what users almost always want for avatar-style clips. The
  // chroma-key path remains available as a cheaper / always-on fallback.
  const [transparency, setTransparency] = useState<VideoClipTransparency>('matting');

  const provider = useMemo(() => PROVIDERS.find(p => p.id === providerId)!, [providerId]);

  // Clamp duration/resolution to whatever the chosen provider supports.
  const safeDuration: Duration = provider.durations.includes(durationSec) ? durationSec : provider.durations[0];
  const safeResolution: Resolution = provider.resolutions.includes(resolution) ? resolution : provider.resolutions[0];

  const trimmed = prompt.trim();
  const canGenerate = trimmed.length >= 3 && !isPending;

  // Live cost estimate — matches backend estimateCostCents per provider.
  // Matting transparency tacks on the veed/fal post-process bill (~$0.018/s),
  // so it must be included in the per-clip estimate for the button label.
  const estimateUsd = useMemo(() => {
    let base: number;
    if (provider.basePriceCents && provider.id === 'kling-fal') {
      const extra = Math.max(0, safeDuration - 5);
      base = (provider.basePriceCents + extra * 5) / 100;
    } else {
      const perSec = provider.costPerSecCents[safeResolution] ?? 4.5;
      base = (safeDuration * perSec) / 100;
    }
    const mattingAddOn = transparency === 'matting' ? safeDuration * 0.018 : 0;
    return base + mattingAddOn;
  }, [provider, safeDuration, safeResolution, transparency]);

  if (!open) return null;

  const handleGenerate = () => {
    if (!canGenerate) return;
    haptics.impact('medium');
    const seedNum = seedInput.trim().length > 0 ? Number(seedInput.trim()) : undefined;
    const seed = typeof seedNum === 'number' && Number.isFinite(seedNum) && Number.isInteger(seedNum)
      ? seedNum
      : undefined;
    onGenerate({
      prompt: trimmed,
      provider: providerId,
      durationSec: safeDuration,
      resolution: safeResolution,
      variants,
      transparency,
      seed,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg/95 backdrop-blur">
      <header className="flex items-center justify-between px-4 py-4">
        <div>
          <h1 className="text-lg font-bold text-fg">Animate 2D</h1>
          <p className="mt-0.5 text-xs text-fg-muted">Skip 3D. From still to MP4 in 30–90s.</p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={() => { haptics.impact('light'); onClose(); }}
          className="rounded-full bg-bg-secondary p-2 text-fg transition-opacity active:opacity-80"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 pb-32">
        <div className="flex flex-col gap-3">
          {/* Provider picker */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted">Engine</label>
            <div className="grid grid-cols-2 gap-2">
              {PROVIDERS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { haptics.selection(); setProviderId(p.id); }}
                  className={cn(
                    'flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2 text-left text-xs transition-colors',
                    providerId === p.id
                      ? 'border-brand bg-brand/10 text-fg'
                      : 'border-border bg-bg-secondary text-fg',
                  )}
                >
                  <span className="flex w-full items-center justify-between">
                    <span className="font-bold">{p.label}</span>
                    <span className="rounded-full bg-bg px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-fg-muted">
                      {TIER_LABEL[p.tier]}
                    </span>
                  </span>
                  <span className="text-[10px] text-fg-muted">{p.blurb}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Motion prompt */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted" htmlFor="a2d-prompt">
              Motion prompt
            </label>
            <textarea
              id="a2d-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              maxLength={MAX_PROMPT_CHARS}
              placeholder="dancing energetically with arms raised, looping"
              className="min-h-[120px] rounded-xl bg-bg-secondary p-3 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
            />
            <p className="text-right text-[11px] text-fg-muted">{trimmed.length} / {MAX_PROMPT_CHARS}</p>
          </div>

          {/* Duration */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted">Duration</label>
            <div className="flex gap-2">
              {provider.durations.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => { haptics.selection(); setDurationSec(d); }}
                  className={cn(
                    'flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors',
                    safeDuration === d
                      ? 'border-brand bg-brand/10 text-fg'
                      : 'border-border bg-bg-secondary text-fg-muted',
                  )}
                >
                  {d}s
                </button>
              ))}
            </div>
          </div>

          {/* Background / transparency — Phase 7c.
              none       → opaque MP4 (default).
              chroma-key → prompt is mutated server-side to force a solid
                           magenta background; the client uses a canvas
                           color-key shader to make it transparent at render
                           time. Free, fast, faux-alpha.
              matting    → backend post-process to true WebM VP9 alpha.
                           ~+$0.10–0.20/clip + 15–25s extra. Real alpha but
                           pipeline ships separately (#185); selecting it
                           today behaves like chroma-key. */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted">Background</label>
            <div className="flex gap-2">
              {([
                { id: 'none', label: 'Original' },
                { id: 'chroma-key', label: 'Transparent' },
                { id: 'matting', label: 'True alpha' },
              ] as const).map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => { haptics.selection(); setTransparency(opt.id); }}
                  className={cn(
                    'flex-1 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors',
                    transparency === opt.id
                      ? 'border-brand bg-brand/10 text-fg'
                      : 'border-border bg-bg-secondary text-fg-muted',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {transparency === 'chroma-key' && (
              <p className="text-[11px] text-fg-muted">
                Renders with a magenta backdrop, removed live on your device. Free, instant. Works best on subjects with no pink.
              </p>
            )}
            {transparency === 'matting' && (
              <p className="text-[11px] text-fg-muted">
                Real per-pixel alpha via veed post-process. Adds ~$0.11 per 6s clip and ~30s wall time, but eliminates magenta halo and color spill on edges. Best quality.
              </p>
            )}
          </div>

          {/* Variants — generate N parallel clips with the same prompt.
              Each variant is billed separately; user gets N chances to find
              the best output without re-typing the prompt. */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted">Variants</label>
            <div className="flex gap-2">
              {([1, 2, 3] as const).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => { haptics.selection(); setVariants(n); }}
                  className={cn(
                    'flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors',
                    variants === n
                      ? 'border-brand bg-brand/10 text-fg'
                      : 'border-border bg-bg-secondary text-fg-muted',
                  )}
                >
                  {n}× {n === 1 ? '' : 'clips'}
                </button>
              ))}
            </div>
            {variants > 1 && (
              <p className="text-[11px] text-fg-muted">
                Bills {variants}× the single-clip cost. Each variant has a different random seed → different output.
              </p>
            )}
          </div>

          {/* Seed — optional integer for reproducible runs. Empty = random
              (provider picks). Useful to A/B engines under matched noise. */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="a2d-seed" className="text-xs font-semibold uppercase text-fg-muted">
              Seed <span className="font-normal normal-case text-fg-muted/60">(optional)</span>
            </label>
            <input
              id="a2d-seed"
              type="number"
              inputMode="numeric"
              value={seedInput}
              onChange={(e) => setSeedInput(e.target.value)}
              placeholder="Random"
              className="rounded-xl bg-bg-secondary px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
            />
          </div>

          {/* Resolution (skipped when provider only supports one) */}
          {provider.resolutions.length > 1 && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase text-fg-muted">Resolution</label>
              <div className="flex gap-2">
                {provider.resolutions.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => { haptics.selection(); setResolution(r); }}
                    className={cn(
                      'flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors',
                      safeResolution === r
                        ? 'border-brand bg-brand/10 text-fg'
                        : 'border-border bg-bg-secondary text-fg-muted',
                    )}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Visible submission error banner */}
          {(() => {
            const err = friendlyError(errorMessage);
            if (!err) return null;
            return (
              <div role="alert" className="flex flex-col gap-1 rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-fg">
                <p className="font-semibold">{err.title}</p>
                {err.detail && <p className="text-xs text-fg-muted">{err.detail}</p>}
              </div>
            );
          })()}

          {/* Tips */}
          <div className="rounded-xl bg-bg-secondary p-3 text-xs leading-relaxed text-fg-muted">
            <p className="font-semibold text-fg">Tips</p>
            <ul className="mt-1 space-y-0.5">
              <li>• Describe the action ("dancing", "raising sword", "spin attack").</li>
              <li>• Add an adverb ("energetically", "slowly", "with attitude").</li>
              <li>• Output is MP4. Loops well when prompt describes cyclic motion.</li>
              <li>• Engine guide: <strong>PixVerse</strong> cheapest, <strong>Hailuo</strong> body movement, <strong>Vidu</strong> multi-shot, <strong>Kling</strong> cinematic.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 border-t border-border bg-bg/95 px-4 py-3 backdrop-blur">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold transition-opacity active:opacity-80',
            canGenerate ? 'bg-brand text-brand-fg' : 'bg-bg-secondary text-fg-muted',
          )}
        >
          {isPending ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : <Sparkles aria-hidden className="h-4 w-4" />}
          Generate {variants > 1 ? `${variants} via ${provider.label}` : `via ${provider.label}`} · ~${(estimateUsd * variants).toFixed(2)}
        </button>
      </div>
    </div>
  );
}
